// packages/frontend/src/App.tsx
import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Container, Grid, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
// Import the functions you need from the SDKs you need
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';

import './App.css';
import { AppUpdatePrompt } from './components/AppUpdatePrompt';
import { BackgroundUploadsTracker } from './components/BackgroundUploads/BackgroundUploadsTracker';
import Header from './components/Header';
import './i18n';
import useFromExternalStore from './stores/FromExternalStore';

// import { getAnalytics } from "firebase/analytics";
// import { getFirestore } from 'firebase/firestore';
// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: 'AIzaSyBB8oZ8v8ph5ZnR6Q1RFGftvaMjdHx0qtQ',
  authDomain: 'skipcar-app.firebaseapp.com',
  projectId: 'skipcar-app',
  storageBucket: 'skipcar-app.appspot.com',
  messagingSenderId: '274069652490',
  appId: '1:274069652490:web:4b4ed185767dba999f4bf3',
  measurementId: 'G-FHL7M0PL2B',
};

/**
 * Taux d'échantillonnage du tracing Sentry. Une valeur d'environnement aberrante
 * (vide, non numérique, hors [0,1]) retombe sur le défaut plutôt que d'être
 * propagée telle quelle à Sentry — même garde que côté backend (instrument.ts).
 */
const resolveTracesSampleRate = (raw: string | undefined, fallback: number): number => {
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback;
};

if (process.env.NODE_ENV !== 'development') {
  Sentry.init({
    dsn: 'https://6523d9dc3993a7540ec3058b675afcdc@o4505963019501568.ingest.sentry.io/4505963020812288',
    // v8+ : ces options sont passées à `init`, plus aux intégrations.
    tracePropagationTargets: [
      /^https:\/\/skipcar-app\.web\.app/,
      /^https:\/\/dev\.app\.skipcar\.fr/,
      /^https:\/\/app\.skipcar\.fr/,
    ],
    integrations: [
      // Sentry v8+ : les intégrations sont des fabriques, `new Sentry.X()` a été retiré.
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Configuration spécifique pour le replay
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
      }),
    ],
    // Performance Monitoring
    //
    // 🔴 Était à 1.0 (100 % des parcours tracés). Le correctif du 2026-08-06
    // (62b7e17b) n'avait ramené que le BACKEND à 1 % : le frontend, lui, a continué
    // de saturer le budget pay-as-you-go partagé par les deux projets de l'org.
    // Résultat, le monitoring est resté aveugle — encore 39 420 spans rejetés le
    // 2026-08-11, et aucune des 45 erreurs MiSIV du 2026-08-12 n'est remontée.
    // Aligné sur le backend : 1 %, surchargeable pour investiguer sans redéployer.
    tracesSampleRate: resolveTracesSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.01),

    // Configuration des replays
    replaysSessionSampleRate: 0.0, // Désactivé par défaut
    replaysOnErrorSampleRate: 1.0, // Activé uniquement pour les erreurs

    // 🔴 Bruit navigateur uniquement (extensions, ResizeObserver, requêtes
    // annulées par une navigation). Tout le reste doit remonter : cf. le
    // beforeSend supprimé ci-dessous.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      'AbortError',
    ],

    // ⚠️ NE PAS réintroduire de beforeSend qui filtre sur `event.exception`.
    // De ffcdcd71 (2025-05-12) au 2026-09-15, le filtre « ne capturer que les
    // erreurs Firebase Storage » rendait `if (!hasStorageError && event.exception)
    // return null`, ce qui jetait TOUTES les exceptions du front. Conséquence
    // mesurée : les 4 cotations perdues de la plaque HB-990-AL (deals Pipedrive
    // 47370, 47371, 47420, 47421) ont planté dans `CreateCotation.postDataDB`
    // sans laisser la moindre trace, alors que l'agence voyait bien « Nos équipes
    // en sont informées ». Le bruit legacy Firebase se traite par `ignoreErrors`
    // ci-dessus, pas par un rejet global.
  });
}

// Initialize Firebase
initializeApp(firebaseConfig);

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const [isBannerVisible, setIsBannerVisible] = useState(false);

  const setFromExternal = useFromExternalStore((state) => state.setFromExternal);
  const isFromExternal = useFromExternalStore((state) => state.fromExternal);

  useEffect(() => {
    if (location.search.includes('from=external')) {
      setFromExternal(true);
      // Traçage : comprendre comment les utilisateurs (notamment des agences) arrivent sur le lien
      // de cotation externe, qui n'a jamais été communiqué.
      //
      // Restreint aux accès CONNECTÉS (12/10/2026) : quatre mois de données montrent
      // que la quasi-totalité des accès sont des vendeurs anonymes en lien direct
      // (referrer vide, « non connecté »), le cas attendu — ~25 événements/jour,
      // 15 % du quota Sentry de 5 000 erreurs/mois, pour une information nulle.
      // Le cas qui intéresse (une vraie agence ou le compte prospection « collés »
      // sur le flux externe) est précisément celui où un utilisateur est connecté.
      if (auth?.currentUser) {
        Sentry.captureMessage('Accès cotation from=external', {
          level: 'info',
          tags: { source: 'from_external' },
          extra: {
            referrer: document.referrer || '(direct/aucun)',
            url: window.location.href,
            userAgent: navigator.userAgent,
            // Qui est connecté au moment de l'accès : permet de distinguer un
            // vendeur externe anonyme, le compte prospection.skipcar@gmail.com,
            // ou une vraie agence "collée" à tort sur le flux externe.
            userEmail: auth?.currentUser?.email || '(non connecté)',
            userUid: auth?.currentUser?.uid || '(aucun)',
          },
        });
      }
    } else {
      setFromExternal(false);
    }
  }, [location.search]);

  useEffect(() => {
    if (isFromExternal && location.pathname !== '/cotation' && location.pathname !== '/login') {
      navigate('/cotation?from=external', { replace: true });
    }
    if (auth?.currentUser?.email === 'prospection.skipcar@gmail.com') {
      if (location.pathname !== '/cotation' && location.pathname !== '/login') {
        setFromExternal(true); // Je suis déjà connecté, j'ai refresh
        navigate('/cotation?from=external', { replace: true });
      }
    }
  }, [location, isFromExternal]);

  const loginAutomatically = async () => {
    if (!isFromExternal) {
      // Si je ne suis pas déjà connecté depuis l'extérieur
      try {
        signOut(auth).then(async () => {
          // Je commence par me déconnecter
          const automaticConnectResult = await signInWithEmailAndPassword(
            auth,
            'prospection.skipcar@gmail.com',
            import.meta.env.VITE_SC_API_KEY,
          );
          if (automaticConnectResult.user.email !== 'prospection.skipcar@gmail.com') {
            // Si l'email ne correspond pas, déconnecter et rediriger vers la page de connexion
            await signOut(auth);
            navigate('/login');
          }
        });
      } catch (error) {
        console.error('Error during automatic authentication:', error);
      }
    }
  };

  const params = new URLSearchParams(location.search);
  const fromExternal = params.get('from') === 'external';

  if (location.pathname === '/login' && fromExternal) {
    setFromExternal(false); // Désactiver le mode "from external"
  } else if (location.pathname === '/cotation' && fromExternal) {
    setFromExternal(true); // Mettre à jour le store
    loginAutomatically();
  }

  const bannerHeight = '67px';
  const showBanner = isBannerVisible && location.pathname !== '/login';

  return (
    <>
      {/* Détection « nouvelle version déployée » → encart de rechargement (cf. version.json). */}
      <AppUpdatePrompt />
      {showBanner && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: bannerHeight,
            minHeight: bannerHeight,
            backgroundColor: '#fdbb5f',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Typography
            variant="body2"
            component="span"
            sx={{
              flex: 1,
              textAlign: 'center',
              fontWeight: 500,
              color: '#000',
              pr: 6,
            }}
          >
            🚨 Un de nos partenaires rencontre actuellement un soucis technique ce qui impacte les demandes de
            cotations. Veuillez nous excuser pour la gêne occasionnée 🚨
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label="Fermer le bandeau d'information"
            onClick={() => setIsBannerVisible(false)}
            sx={{
              position: 'absolute',
              right: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <CloseIcon
              sx={{
                color: '#000',
                fontSize: 28,
                display: 'block',
              }}
            />
          </Box>
        </Box>
      )}
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          padding: 0,
          paddingTop: showBanner ? bannerHeight : 0,
          width: '100%',
        }}
      >
        <Grid id="header">
          <Header />
        </Grid>
        <Grid id="main">
          <Outlet />
        </Grid>
      </Container>
      <BackgroundUploadsTracker />
    </>
  );
};

export default App;
